/* ==========================================================================
   Drawer controller — shared by the nav drawer (C1) and the cart drawer (C2).

   Handles the parts that are easy to get wrong and unpleasant when you do:
   focus moves into the panel and is trapped there, Escape closes, the page
   behind does not scroll, and focus returns to whatever opened it.

   Markup contract:
     [data-gp-drawer]        the container, with a unique id
     [data-gp-drawer-open]   opener; aria-controls points at that id
     [data-gp-drawer-close]  any closer inside (scrim, ×, cancel)
   ========================================================================== */

(() => {
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
    'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  let openDrawer = null;
  let lastFocused = null;

  const focusableIn = (el) =>
    [...el.querySelectorAll(FOCUSABLE)].filter(
      (n) => n.offsetParent !== null || n.getClientRects().length
    );

  function open(drawer, opener) {
    if (openDrawer) close(openDrawer);

    lastFocused = opener || document.activeElement;
    drawer.hidden = false;

    // Force a frame so the transition runs from the closed state rather than
    // snapping straight to open.
    requestAnimationFrame(() => drawer.classList.add('is-open'));

    document.body.style.overflow = 'hidden';
    openDrawer = drawer;

    if (opener) opener.setAttribute('aria-expanded', 'true');

    const panel = drawer.querySelector('[role="dialog"]') || drawer;
    const first = focusableIn(panel)[0];
    (first || panel).focus?.();
    if (!first) panel.setAttribute('tabindex', '-1');

    drawer.dispatchEvent(new CustomEvent('gp:drawer:open', { bubbles: true }));
  }

  function close(drawer) {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    document.body.style.overflow = '';

    document
      .querySelectorAll(`[data-gp-drawer-open][aria-controls="${drawer.id}"]`)
      .forEach((b) => b.setAttribute('aria-expanded', 'false'));

    const done = () => {
      drawer.hidden = true;
      drawer.removeEventListener('transitionend', done);
    };
    // transitionend never fires under prefers-reduced-motion, so don't wait on it.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) done();
    else {
      drawer.addEventListener('transitionend', done);
      setTimeout(done, 500); // belt and braces if the transition is interrupted
    }

    if (openDrawer === drawer) openDrawer = null;
    lastFocused?.focus?.();
    lastFocused = null;

    drawer.dispatchEvent(new CustomEvent('gp:drawer:close', { bubbles: true }));
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-gp-drawer-open]');
    if (opener) {
      const drawer = document.getElementById(opener.getAttribute('aria-controls'));
      // No drawer on this page (or JS-less fallback link) — let the href work.
      if (drawer) {
        e.preventDefault();
        open(drawer, opener);
      }
      return;
    }

    const closer = e.target.closest('[data-gp-drawer-close]');
    if (closer) {
      e.preventDefault();
      close(closer.closest('[data-gp-drawer]'));
    }
  });

  document.addEventListener('keydown', (e) => {
    if (!openDrawer) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      close(openDrawer);
      return;
    }

    if (e.key !== 'Tab') return;

    const panel = openDrawer.querySelector('[role="dialog"]') || openDrawer;
    const items = focusableIn(panel);
    if (!items.length) return;

    const first = items[0];
    const last = items[items.length - 1];

    // Wrap, so Tab can never walk out of the drawer into the page behind it.
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    } else if (!panel.contains(document.activeElement)) {
      e.preventDefault();
      first.focus();
    }
  });

  // Expose for the cart drawer, which needs to open itself after add-to-cart.
  window.gpDrawer = { open, close, get current() { return openDrawer; } };
})();
